/**
 * ChatContext — global state for the AI chat assistant.
 *
 * Responsibilities:
 * - manage chat sessions list
 * - manage messages for the active session
 * - open/close drawer
 * - stream responses from the backend via native Fetch + ReadableStream (SSE)
 * - persist the active session id in sessionStorage
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { message as antMessage } from 'antd';
import chatApi, { ChatSession, ChatMessage, PageContext } from '@/services/chatApi';

// ─────────────────────────── types ───────────────────────────────────────────

export interface ChatContextValue {
  // drawer
  isOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  panelMode: 'compact' | 'expanded';
  setPanelMode: (mode: 'compact' | 'expanded') => void;
  togglePanelMode: () => void;

  // sessions
  sessions: ChatSession[];
  activeSessionId: number | null;
  setActiveSession: (id: number | null) => void;
  createSession: () => Promise<number>;
  deleteSession: (id: number) => Promise<void>;
  loadSessions: () => Promise<void>;

  // messages
  messages: ChatMessage[];
  streamingContent: string;
  isStreaming: boolean;

  // send
  sendMessage: (content: string, pageContext: PageContext) => Promise<void>;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

const SESSION_STORAGE_KEY = 'chat_active_session_id';
const PANEL_MODE_STORAGE_KEY = 'chat_panel_mode';

// ─────────────────────────── provider ────────────────────────────────────────

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelModeState, setPanelModeState] = useState<'compact' | 'expanded'>(() => {
    try {
      const stored = localStorage.getItem(PANEL_MODE_STORAGE_KEY);
      return stored === 'expanded' ? 'expanded' : 'compact';
    } catch {
      return 'compact';
    }
  });
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionIdState] = useState<number | null>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return stored ? Number(stored) : null;
    } catch {
      return null;
    }
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  // AbortController for cancelling ongoing stream
  const abortRef = useRef<AbortController | null>(null);

  // ── session persistence ────────────────────────────────────────────────────
  const setActiveSession = useCallback((id: number | null) => {
    setActiveSessionIdState(id);
    try {
      if (id != null) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, String(id));
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch { /* ignore */ }
  }, []);

  const setPanelMode = useCallback((mode: 'compact' | 'expanded') => {
    setPanelModeState(mode);
    try {
      localStorage.setItem(PANEL_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore storage failures
    }
  }, []);

  const togglePanelMode = useCallback(() => {
    setPanelMode(panelModeState === 'expanded' ? 'compact' : 'expanded');
  }, [panelModeState, setPanelMode]);

  // ── load sessions ──────────────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    try {
      const list = await chatApi.listSessions();
      setSessions(list);
    } catch {
      // silently ignore — user may not be authenticated yet
    }
  }, []);

  // ── load messages when active session changes ──────────────────────────────
  useEffect(() => {
    if (activeSessionId == null) {
      setMessages([]);
      return;
    }
    chatApi.getMessages(activeSessionId)
      .then(setMessages)
      .catch(() => setMessages([]));
  }, [activeSessionId]);

  // ── load sessions when drawer opens ───────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      loadSessions();
    }
  }, [isOpen, loadSessions]);

  // ── create session ─────────────────────────────────────────────────────────
  const createSession = useCallback(async (): Promise<number> => {
    const session = await chatApi.createSession();
    setSessions(prev => [session, ...prev]);
    setActiveSession(session.id);
    setMessages([]);
    return session.id;
  }, [setActiveSession]);

  // ── delete session ─────────────────────────────────────────────────────────
  const deleteSession = useCallback(async (id: number) => {
    await chatApi.deleteSession(id);
    setSessions(prev => prev.filter(s => s.id !== id));
    if (activeSessionId === id) {
      setActiveSession(null);
      setMessages([]);
    }
  }, [activeSessionId, setActiveSession]);

  // ── open / close ───────────────────────────────────────────────────────────
  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => {
    setIsOpen(false);
    // cancel any ongoing stream
    abortRef.current?.abort();
  }, []);

  // ── send message (streaming) ───────────────────────────────────────────────
  const sendMessage = useCallback(async (content: string, pageContext: PageContext) => {
    if (isStreaming) return;

    let sessionId = activeSessionId;

    // Ensure we have an active session
    if (sessionId == null) {
      sessionId = await createSession();
    }

    // Optimistically add user message to local state
    const tempUserMsg: ChatMessage = {
      id: Date.now(), // temporary id
      session_id: sessionId,
      role: 'user',
      content,
      page_context: null,
      tokens_used: null,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);
    setStreamingContent('');
    setIsStreaming(true);

    const abort = new AbortController();
    abortRef.current = abort;

    try {
      const tenantId = localStorage.getItem('selectedTenantId');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (tenantId) headers['X-Tenant-ID'] = tenantId;
      // Inject Bearer token from sessionStorage (same fallback axios uses)
      // Needed when cookie is not forwarded by Vercel → Render proxy
      try {
        const token = window.sessionStorage?.getItem('auth_token');
        if (token) headers['Authorization'] = `Bearer ${token}`;
      } catch { /* ignore */ }

      const response = await fetch(`/api/v1/chat/sessions/${sessionId}/stream`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ content, page_context: pageContext }),
        signal: abort.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let accumulated = '';

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        streamDone = done;
        if (streamDone) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const parsed = JSON.parse(jsonStr);

            if (parsed.type === 'chunk') {
              accumulated += parsed.content ?? '';
              setStreamingContent(accumulated);
            } else if (parsed.type === 'done') {
              // Replace streaming content with real assistant message
              const assistantMsg: ChatMessage = {
                id: Date.now() + 1,
                session_id: sessionId!,
                role: 'assistant',
                content: accumulated,
                page_context: null,
                tokens_used: parsed.tokens_used ?? null,
                created_at: new Date().toISOString(),
              };
              setMessages(prev => [...prev, assistantMsg]);
              setStreamingContent('');

              // Refresh sessions list to update title + message_count
              loadSessions();
            } else if (parsed.type === 'error') {
              antMessage.error(parsed.content ?? 'Erro ao processar resposta da IA.');
              setStreamingContent('');
            }
          } catch {
            // malformed JSON line — skip
          }
        }
      }
    } catch (err: unknown) {
      if ((err as any)?.name !== 'AbortError') {
        antMessage.error('Não foi possível conectar ao assistente. Tente novamente.');
      }
      setStreamingContent('');
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [isStreaming, activeSessionId, createSession, loadSessions]);

  // ── context value ──────────────────────────────────────────────────────────
  const value: ChatContextValue = {
    isOpen,
    openChat,
    closeChat,
    panelMode: panelModeState,
    setPanelMode,
    togglePanelMode,
    sessions,
    activeSessionId,
    setActiveSession,
    createSession,
    deleteSession,
    loadSessions,
    messages,
    streamingContent,
    isStreaming,
    sendMessage,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

// ─────────────────────────── hook ────────────────────────────────────────────

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used inside ChatProvider');
  return ctx;
}
